import { useMemo, useState } from "react";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { getManufacturingRecipeDetail } from "@/lib/manufacturingClient";
import { seedNamedVariantTransformFromRecipeLines } from "@/lib/manufacturing/namedVariantSeedFromRecipe";
import type { VariantConfig } from "@/lib/manufacturing/variantConfig";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";

export function ManufacturingNamedVariantExampleRecipePicker({
  recipes,
  disabled,
  onSeeded,
}: {
  recipes: MfgRecipeSummary[];
  disabled?: boolean;
  onSeeded: (
    seed: { transform: VariantConfig; recipeSlots: string[]; hint: string } | null,
  ) => void;
}) {
  const [recipeId, setRecipeId] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidates = useMemo(
    () =>
      recipes.filter(
        (r) => r.is_active && r.kind !== "variant" && r.kind !== "option",
      ),
    [recipes],
  );

  async function onPick(id: string) {
    setRecipeId(id);
    setError(null);
    if (!id) {
      setHint(null);
      onSeeded(null);
      return;
    }
    setBusy(true);
    const res = await getManufacturingRecipeDetail(id);
    setBusy(false);
    if (!res.ok) {
      setHint(null);
      setError(res.error);
      onSeeded(null);
      return;
    }
    const seed = seedNamedVariantTransformFromRecipeLines(res.data.lines);
    if (!seed) {
      setHint(null);
      setError("That recipe has no material BOM lines to seed from.");
      onSeeded(null);
      return;
    }
    const parts = [
      seed.exampleMaterialLabel,
      seed.exampleSlot ? `slot ${seed.exampleSlot}` : null,
      seed.exampleFromNominal ? `from ${seed.exampleFromNominal}` : null,
      seed.recipeSlots.length > 0 ? `${seed.recipeSlots.length} slots prioritized` : null,
    ].filter(Boolean);
    const nextHint = `Prefilling transform from first BOM material: ${parts.join(" · ")}`;
    setHint(nextHint);
    onSeeded({
      transform: seed.transform,
      recipeSlots: seed.recipeSlots,
      hint: nextHint,
    });
  }

  return (
    <div className="space-y-1">
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
          Start from example recipe
        </span>
        <select
          className={mfgInlineInputClass}
          value={recipeId}
          disabled={Boolean(disabled) || busy}
          onChange={(e) => void onPick(e.target.value)}
        >
          <option value="">Optional — pick a company recipe…</option>
          {candidates.map((r) => (
            <option key={r.id} value={r.id}>
              {r.code} — {r.name}
            </option>
          ))}
        </select>
      </label>
      {busy ? (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">Reading BOM…</p>
      ) : null}
      {hint ? (
        <p className="text-[11px] text-indigo-700 dark:text-indigo-300">{hint}</p>
      ) : null}
      {error ? (
        <p className="text-[11px] text-red-700 dark:text-red-300">{error}</p>
      ) : null}
      <p className="text-[11px] text-gray-500 dark:text-zinc-400">
        Uses the first material line so From / slot match your catalog language. Set the To size
        after create.
      </p>
    </div>
  );
}
